import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useColorMode, Box, Spinner } from '@chakra-ui/react';
import { FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { quillModules, quillFormats } from '../Blog/quillConfig';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function AjouterJournal() {
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const [title, setTitle] = useState('');
  const [period, setPeriod] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const bg = isDark ? '#0a0f16' : '#f7faff';
  const cardBg = isDark ? '#111827' : '#ffffff';
  const textPrimary = isDark ? '#f1f5f9' : '#0f172a';
  const textMuted = isDark ? '#94a3b8' : '#64748b';
  const inputBg = isDark ? '#1e293b' : '#f8fafc';
  const inputBorder = isDark ? '#334155' : '#e2e8f0';

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/connexion'); return; }
    try {
      const decoded = JSON.parse(atob(token.split('.')[1]));
      if (!decoded.adminId) navigate('/');
    } catch { navigate('/'); }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !period || !content) return;
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API_URL}/api/journal`, { title, period, excerpt, content }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setSuccess(true);
      setTimeout(() => navigate('/journal'), 1500);
    } catch (err) {
      alert('Erreur lors de la publication.');
    } finally {
      setLoading(false);
    }
  };

  const labelStyle = { display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: textMuted };
  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: '10px',
    border: `1px solid ${inputBorder}`, background: inputBg,
    color: textPrimary, fontSize: '14px', outline: 'none',
  };

  return (
    <Box minH="100vh" bg={bg} pt="80px">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <button onClick={() => navigate('/journal')} className="flex items-center gap-2 text-sm font-semibold text-blue-600 hover:gap-3 transition-all mb-8">
          <FaArrowLeft size={13} /> Retour au journal
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl font-black mb-8" style={{ color: textPrimary }}>Publier un nouveau journal</h1>

          {success ? (
            <div className="flex flex-col items-center gap-3 py-20 text-green-500">
              <FaCheckCircle size={48} />
              <p className="font-bold text-lg">Journal publié avec succès !</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-6" style={{ background: cardBg, borderRadius: '16px', padding: '32px' }}>
              <div>
                <label style={labelStyle}>Titre *</label>
                <input style={inputStyle} value={title} onChange={e => setTitle(e.target.value)} placeholder="Ex : Journal HIS — T2 2026" required />
              </div>

              <div>
                <label style={labelStyle}>Période *</label>
                <input style={inputStyle} value={period} onChange={e => setPeriod(e.target.value)} placeholder="Ex : T2 2026" required />
              </div>

              <div>
                <label style={labelStyle}>Résumé (affiché sur la liste)</label>
                <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
                  value={excerpt} onChange={e => setExcerpt(e.target.value)}
                  placeholder="Une ou deux phrases d'accroche..." />
              </div>

              <div>
                <label style={labelStyle}>Contenu *</label>
                <div style={{ borderRadius: '10px', overflow: 'hidden', border: `1px solid ${inputBorder}` }}>
                  <ReactQuill
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    modules={quillModules}
                    formats={quillFormats}
                    style={{ background: inputBg, color: textPrimary, minHeight: '300px' }}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !title || !period || !content}
                className="mt-2 w-full py-3 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center gap-2"
              >
                {loading ? <Spinner size="sm" /> : 'Publier le journal'}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </Box>
  );
}
